import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, AlertTriangle, BookOpen, Scale, Wallet, LogOut, Phone, Mail, Loader2 } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { type HoldType } from '@/services/userService';

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api`;

interface ActiveHold {
  id: string;
  holdType: HoldType;
  reason: string | null;
  placedAt: string;
}

const holdTypeConfig: Record<HoldType, { label: string; icon: React.ElementType; color: string; bgColor: string; description: string }> = {
  ACADEMIC: {
    label: 'Blocage académique',
    icon: BookOpen,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Votre compte est bloqué pour des raisons académiques (documents manquants, probation académique, etc.)',
  },
  DISCIPLINARY: {
    label: 'Blocage disciplinaire',
    icon: Scale,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    description: 'Votre compte est bloqué suite à une mesure disciplinaire.',
  },
  FINANCIAL: {
    label: 'Blocage financier',
    icon: Wallet,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    description: 'Votre compte est bloqué en raison de frais impayés.',
  },
};

export const SuspendedAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, tokens, logout } = useAuthStore();
  const [holds, setHolds] = useState<ActiveHold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Redirect if user is not suspended or not logged in
  useEffect(() => {
    if (!user || !tokens) {
      navigate('/login');
      return;
    }

    if (user.status !== 'SUSPENDED') {
      // User is not suspended, redirect to their dashboard
      navigate('/');
      return;
    }
  }, [user, tokens, navigate]);

  // Fetch user's active holds
  useEffect(() => {
    const fetchHolds = async () => {
      if (!tokens?.accessToken) return;

      try {
        const response = await fetch(`${API_BASE}/auth/my-holds`, {
          headers: {
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch holds');
        }

        const data = await response.json();
        setHolds(data.activeHolds || []);
      } catch (err) {
        console.error('Error fetching holds:', err);
        setError('Impossible de charger les informations de blocage');
      } finally {
        setLoading(false);
      }
    };

    fetchHolds();
  }, [tokens]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user || user.status !== 'SUSPENDED') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-8 text-center">
            <div className="h-20 w-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert size={40} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Compte suspendu
            </h1>
            <p className="text-red-100">
              {user.firstName} {user.lastName}
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={32} className="animate-spin text-gray-400" />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <AlertTriangle className="mx-auto text-red-500 mb-2" size={24} />
                <p className="text-red-700">{error}</p>
              </div>
            ) : (
              <>
                {/* Holds List */}
                <div className="space-y-4 mb-6">
                  <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Blocages actifs sur votre compte
                  </h2>

                  {holds.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500">
                      Aucun blocage trouvé. Contactez l'administration pour plus d'informations.
                    </div>
                  ) : (
                    holds.map((hold) => {
                      const config = holdTypeConfig[hold.holdType];
                      const Icon = config.icon;

                      return (
                        <div
                          key={hold.id}
                          className={`${config.bgColor} rounded-lg p-4 border border-opacity-20`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 bg-white rounded-lg ${config.color}`}>
                              <Icon size={24} />
                            </div>
                            <div className="flex-1">
                              <h3 className={`font-semibold ${config.color}`}>
                                {config.label}
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                {config.description}
                              </p>
                              {hold.reason && (
                                <div className="mt-2 bg-white/60 rounded p-2">
                                  <p className="text-sm text-gray-700">
                                    <span className="font-medium">Motif: </span>
                                    {hold.reason}
                                  </p>
                                </div>
                              )}
                              <p className="text-xs text-gray-500 mt-2">
                                Depuis le {new Date(hold.placedAt).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                })}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Contact Information */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">
                    Pour lever ce blocage, contactez l'administration
                  </h3>
                  <div className="space-y-2">
                    <a
                      href="tel:+212000000000"
                      className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                    >
                      <Phone size={18} />
                      <span>Appelez le service scolarité</span>
                    </a>
                    <a
                      href="mailto:scolarite@etablissement.ma"
                      className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                    >
                      <Mail size={18} />
                      <span>Envoyez un email</span>
                    </a>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex gap-3">
                    <AlertTriangle className="text-yellow-600 flex-shrink-0" size={20} />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Accès limité</p>
                      <p>
                        Tant que votre compte est suspendu, vous ne pouvez pas accéder aux services de l'établissement.
                        Veuillez régulariser votre situation auprès de l'administration.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <LogOut size={18} />
              <span>Se déconnecter</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Si vous pensez qu'il s'agit d'une erreur, contactez l'administration de votre établissement.
        </p>
      </div>
    </div>
  );
};

export default SuspendedAccountPage;
